import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { TrendingUp, LogOut, Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import TradingAppBar from './TradingAppBar';
import LogoutConfirmationModal from './LogoutConfirmationModal';
import { menuItems as allMenuItems, ALWAYS_VISIBLE_PATHS } from '../config/menuItems';
import { pageVisibilityAPI } from '../services/pageVisibilityApi';

interface MainLayoutProps {
  children: React.ReactNode;
  pnlRs?: number;
  pnlPercent?: number;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, pnlRs = 0, pnlPercent = 0 }) => {
  const { user, logout } = useAuth();
  const { themeConfig } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // Tablet/narrow screens default to the compact icon-rail sidebar so pages get their full
  // width immediately — the user can still expand it manually via the toggle button.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 992);
  // Client's explicit ask: even when manually expanded on a tablet, the sidebar was still taking
  // the same 240px as desktop, eating too much of the page width. Narrower on tablet widths
  // regardless of collapsed state — tracked via resize so rotating a tablet updates it live.
  const [isTabletWidth, setIsTabletWidth] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 992);
  useEffect(() => {
    const onResize = () => setIsTabletWidth(window.innerWidth <= 992);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const sidebarWidth = sidebarCollapsed ? '60px' : (isTabletWidth ? '180px' : '240px');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Settings → Page Visibility — hidden pages (plus Dashboard/Settings, always forced visible)
  // are dropped from the sidebar here; starts as "show everything" so there's no flash of a
  // shorter menu before the GET resolves.
  const [hiddenPaths, setHiddenPaths] = useState<Set<string>>(new Set());

  React.useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchVisibility = () => {
      pageVisibilityAPI.list().then(res => {
        setHiddenPaths(new Set(res.data.filter(p => !p.is_visible).map(p => p.page_key)));
      }).catch(() => {});
    };
    fetchVisibility();
    // SettingsPage dispatches this on every successful toggle — MainLayout wraps SettingsPage
    // itself too, so without this the sidebar would only pick up a change on the next full page
    // load/remount, not immediately after flipping a switch.
    window.addEventListener('page-visibility-changed', fetchVisibility);
    return () => window.removeEventListener('page-visibility-changed', fetchVisibility);
  }, []);

  const menuItems = allMenuItems.filter(item => ALWAYS_VISIBLE_PATHS.includes(item.path) || !hiddenPaths.has(item.path));

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    logout();
    navigate('/login');
  };

  return (
    <div className="d-flex" style={{ minHeight: '100vh', fontSize: '13px' }}>
      {/* Sidebar */}
      <div
        style={{
          width: sidebarWidth,
          transition: 'width 0.3s ease',
          position: 'fixed',
          height: '100vh',
          zIndex: 1000,
          background: themeConfig.colors.sidebar,
          borderRight: `1px solid ${themeConfig.colors.border}`,
          boxShadow: `0 0 10px ${themeConfig.colors.shadow}`
        }}
        className="d-flex flex-column"
      >
        {/* Header */}
        <div className="p-2 border-bottom">
          <div className="d-flex align-items-center justify-content-between">
            {!sidebarCollapsed && (
              <div style={{ flex: 1 }}>
                <div className="d-flex align-items-center">
                  <div style={{ background: themeConfig.colors.primary, borderRadius: '4px', padding: '4px', color: 'white', marginRight: '8px' }}>
                    <TrendingUp size={16} />
                  </div>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>AutoTrade</span>
                </div>
                <div style={{ fontSize: '11px', color: themeConfig.colors.danger, marginLeft: '32px', marginTop: '4px', fontWeight: '500' }}>
                  {lastUpdate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                </div>
              </div>
            )}
            <button
              className="btn btn-link p-1"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{ border: 'none', color: themeConfig.colors.text }}
            >
              {sidebarCollapsed ? <Menu size={16} /> : <X size={16} />}
            </button>
          </div>
        </div>

        {/* User Info */}
        {!sidebarCollapsed && (
          <div className="p-2 border-bottom">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  background: themeConfig.colors.primary,
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '8px'
                }}
              >
                <span style={{ fontWeight: '600', color: 'white', fontSize: '12px' }}>
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '12px' }}>{user?.username}</div>
                <small style={{ color: themeConfig.colors.textSecondary, fontSize: '11px' }}>Trader</small>
              </div>
            </div>
          </div>
        )}

        {/* Navigation — overflowY:'auto' + minHeight:0 so this scrolls internally once the menu
            list (now many features deep) outgrows the fixed 100vh sidebar height, instead of
            silently clipping the bottom items (Broker Management/Settings/Logout) with no way to
            reach them. minHeight:0 is required here: a flex-grow item inside a flex-column parent
            ignores overflow and just expands past its container without it. */}
        <nav className="flex-grow-1 p-1" style={{ overflowY: 'auto', minHeight: 0 }}>
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <button
                key={item.path}
                className="btn w-100 text-start d-flex align-items-center"
                onClick={() => navigate(item.path)}
                style={{
                  padding: sidebarCollapsed ? '6px' : '6px 8px',
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  fontSize: '12px',
                  fontWeight: '500',
                  margin: '1px 0',
                  borderRadius: '4px',
                  border: isActive ? 'none' : (item.color ? `1px solid ${item.color}20` : 'none'),
                  background: isActive ? (item.color || themeConfig.colors.primary) : 'transparent',
                  color: isActive ? 'white' : themeConfig.colors.sidebarText
                }}
              >
                <IconComponent size={16} />
                {!sidebarCollapsed && <span className="ms-2">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-1 border-top">
          <button
            className="btn w-100 d-flex align-items-center"
            onClick={handleLogoutClick}
            style={{
              padding: sidebarCollapsed ? '6px' : '6px 8px',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              background: 'transparent',
              border: `1px solid ${themeConfig.colors.danger}`,
              color: themeConfig.colors.danger
            }}
          >
            <LogOut size={16} />
            {!sidebarCollapsed && <span className="ms-2">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        className="flex-grow-1"
        style={{
          marginLeft: sidebarWidth,
          // minWidth: 0 overrides the flexbox default (min-width: auto), which otherwise sizes
          // this flex item to its content's natural width instead of letting it shrink — that
          // silently breaks flexWrap everywhere inside (e.g. NiftyBuyingPage's NIFTY/SENSEX
          // columns), forcing a page-wide horizontal scrollbar on tablet instead of wrapping.
          minWidth: 0,
          transition: 'margin-left 0.3s ease',
          background: themeConfig.colors.background,
          minHeight: '100vh',
          color: themeConfig.colors.text
        }}
      >
        {/* Header with Trading Controls */}
        <TradingAppBar
          pnlRs={pnlRs}
          pnlPercent={pnlPercent}
          onTradeToggle={(enabled) => console.log('Trade:', enabled)}
          onCallToggle={(enabled) => console.log('Call:', enabled)}
          onPutToggle={(enabled) => console.log('Put:', enabled)}
          onExit50={() => console.log('Exit 50%')}
          onExitAll={() => console.log('Exit All')}
        />

        {/* Page Content */}
        {children}
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        show={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        username={user?.username}
      />
    </div>
  );
};

export default MainLayout;